import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import {
  TDateOrder,
  TFormatUpdatedTimestampOptions,
} from '@/shared/hooks/use-date-utils/use-date-utils.types';
import { BillingPeriodEnum } from '@/shared/types/types.generated';
import { normalizeTimestamp } from '@/shared/utils/format';

// Returns the ms until the next relative-time boundary for a timestamp, or null
// once it's past the largest threshold (so no further re-render is scheduled).
export const getNextTickMs = (
  timestamp: string | number,
  options?: TFormatUpdatedTimestampOptions,
): number | null => {
  const diffMs = Date.now() - new Date(normalizeTimestamp(timestamp)).getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diffMs < minute && !options?.ignoreThresholds?.minute) {
    return minute - diffMs;
  }
  if (diffMs < hour && !options?.ignoreThresholds?.hour) {
    return minute - (diffMs % minute);
  }
  if (diffMs < day && !options?.ignoreThresholds?.day) {
    return hour - (diffMs % hour);
  }
  return null;
};

// Derives the calendar-field order (dmy / mdy / ymd) from the user's stored
// date format (e.g. "dd/MM/yyyy" → dmy).
export const getDateOrder = (dateTimeFormat: string): TDateOrder => {
  const spaceIdx = dateTimeFormat.indexOf(' ');
  const dateOnly =
    spaceIdx >= 0 ? dateTimeFormat.slice(0, spaceIdx) : dateTimeFormat;
  const dayIdx = dateOnly.indexOf('d');
  const monthIdx = dateOnly.indexOf('M');
  const yearIdx = dateOnly.indexOf('y');
  if (yearIdx < monthIdx) return 'ymd';
  if (monthIdx < dayIdx) return 'mdy';
  return 'dmy';
};

// Splits the user's stored datetime format into its calendar-field order, the
// time sub-format, and the date-only portion
// (e.g. "dd/MM/yyyy HH:mm" → { order: 'dmy', timePart: 'HH:mm', dateOnly: 'dd/MM/yyyy' }).
const extractDateFormats = (dateTimeFormat: string) => {
  const spaceIdx = dateTimeFormat.indexOf(' ');
  const dateOnly =
    spaceIdx >= 0 ? dateTimeFormat.slice(0, spaceIdx) : dateTimeFormat;
  const timePart = spaceIdx >= 0 ? dateTimeFormat.slice(spaceIdx + 1) : 'HH:mm';

  return { order: getDateOrder(dateTimeFormat), timePart, dateOnly };
};

// Builds a compact numeric date (e.g. "27/05/26") from the user's stored date
// format, preserving their field order and separator but shortening the year
// to two digits.
const formatShortNumericDate = (date: Date, dateOnly: string): string => {
  const shortFormat = dateOnly.replace(/y+/, 'yy');
  return format(toZonedTime(date, 'UTC'), shortFormat);
};

// Compact numeric date plus the user's preferred time format
// (e.g. "27/05/26 14:30").
const formatShortNumericDateTime = (
  date: Date,
  dateOnly: string,
  timePart: string,
): string => {
  return `${formatShortNumericDate(date, dateOnly)} ${format(toZonedTime(date, 'UTC'), timePart)}`;
};

// Formats day + localised month name + year in the user's preferred field
// order. Month name follows the UI language via Intl.
export const formatDateWithOrder = (
  date: Date,
  locale: string,
  order: TDateOrder,
): string => {
  const parts = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).formatToParts(date);

  const day = parts.find((p) => p.type === 'day')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const year = parts.find((p) => p.type === 'year')?.value ?? '';

  if (order === 'dmy') return `${day} ${month} ${year}`;
  if (order === 'mdy') return `${month} ${day}, ${year}`;
  return `${year} ${month} ${day}`;
};

// Formats localised month name + two-digit year in the user's preferred field
// order (e.g. "Jan 26"). Used for month buckets that fall on the 1st.
const formatMonthYearShort = (
  date: Date,
  locale: string,
  order: TDateOrder,
): string => {
  const parts = new Intl.DateTimeFormat(locale, {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  }).formatToParts(date);

  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const year = parts.find((p) => p.type === 'year')?.value ?? '';

  return order === 'ymd' ? `${year} ${month}` : `${month} ${year}`;
};

// Formats a usage-report bucket timestamp according to its period, honouring
// the user's stored date/time preferences.
export const formatTimestampByPeriod = (
  ts: string,
  locale: string,
  period: BillingPeriodEnum | undefined,
  sinceLabel: (date: string) => string,
  dateTimeFormat: string,
): string => {
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return ts;

  const { order, timePart, dateOnly } = extractDateFormats(dateTimeFormat);

  switch (period) {
    case BillingPeriodEnum.Plan:
    case BillingPeriodEnum.Tenancy:
      return sinceLabel(formatShortNumericDate(date, dateOnly));
    case BillingPeriodEnum.Year:
    case BillingPeriodEnum.Week:
    case BillingPeriodEnum.Day:
      return formatShortNumericDate(date, dateOnly);
    case BillingPeriodEnum.Month: {
      if (date.getUTCDate() === 1) {
        return formatMonthYearShort(date, locale, order);
      }
      return formatShortNumericDate(date, dateOnly);
    }
    case BillingPeriodEnum.Hour:
    case BillingPeriodEnum.Minute:
    default:
      return formatShortNumericDateTime(date, dateOnly, timePart);
  }
};
